import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check } from '@/components/ui/icons';
import type { Status, StatusType } from '@shared/types';
import { STATUS_GROUPS } from '@shared/constants';
import { isNeutral, readableOn } from '@/lib/color';
import { MenuSearch, useListNav } from '@/components/ui/Menu';

/** Fraction filled for active statuses so the icon reads as progress through the workflow. */
export function statusProgress(statuses: Status[], status: Status): number {
  if (status.type !== 'custom') return 0;
  const active = statuses.filter((s) => s.type === 'custom').sort((a, b) => a.orderIndex - b.orderIndex);
  const i = active.findIndex((s) => s.id === status.id || s.name === status.name);
  return active.length ? (Math.max(0, i) + 1) / (active.length + 1) : 0.5;
}

export function StatusIcon({
  type,
  color,
  size = 14,
  progress = 0.5,
}: {
  type: StatusType;
  color: string;
  size?: number;
  progress?: number;
}) {
  const r = 5.5;
  const c = 7;
  if (type === 'open' || type === 'unstarted') {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" className="status-icon">
        <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth="1.6" strokeDasharray="2.2 1.9" />
      </svg>
    );
  }
  if (type === 'custom') {
    const a = Math.min(0.999, Math.max(0.05, progress)) * Math.PI * 2;
    const ir = 3.2;
    const x = c + ir * Math.sin(a);
    const y = c - ir * Math.cos(a);
    const large = a > Math.PI ? 1 : 0;
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" className="status-icon">
        <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth="1.6" />
        <path d={`M${c} ${c} L${c} ${c - ir} A${ir} ${ir} 0 ${large} 1 ${x} ${y} Z`} fill={color} />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" className="status-icon">
      <circle cx={c} cy={c} r={6.2} fill={color} />
      <path d="M4.3 7.2 6.2 9 9.8 5.3" fill="none" stroke={type === 'closed' || type === 'done' ? readableOn(color) : '#fff'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function StatusPill({
  status,
  statuses,
  size = 'md',
  className,
  iconAfter,
  onClick,
}: {
  status: Status;
  statuses?: Status[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  iconAfter?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const neutral = isNeutral(status.color);
  const fg = neutral ? 'var(--text-1)' : readableOn(status.color);
  const icon = (
    <StatusIcon
      type={status.type}
      color={neutral ? 'currentColor' : fg}
      size={size === 'lg' ? 14 : 12}
      progress={statuses ? statusProgress(statuses, status) : 0.5}
    />
  );
  return (
    <span
      className={clsx('status-pill', `status-pill-${size}`, { neutral }, className)}
      style={neutral ? undefined : { background: status.color, color: fg }}
      onClick={onClick}
    >
      {!iconAfter && icon}
      <span className="truncate">{status.name}</span>
      {iconAfter && icon}
    </span>
  );
}

/** Body of the status dropdown: search + statuses grouped Not started / Active / Done / Closed. */
export function StatusMenu({
  statuses,
  currentId,
  onPick,
}: {
  statuses: Status[];
  currentId?: string;
  onPick: (s: Status) => void;
}) {
  const [q, setQ] = useState('');
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return STATUS_GROUPS.map((g) => ({
      ...g,
      items: statuses.filter((s) => g.types.includes(s.type) && (!ql || s.name.toLowerCase().includes(ql))).sort((a, b) => a.orderIndex - b.orderIndex),
    })).filter((g) => g.items.length);
  }, [statuses, q]);
  const flat = groups.flatMap((g) => g.items);
  const nav = useListNav(flat.length, (i) => onPick(flat[i]));
  let k = -1;
  return (
    <div className="menu status-menu" onKeyDown={nav.onKeyDown} style={{ width: 240 }}>
      <MenuSearch value={q} onChange={setQ} placeholder="Search..." />
      {groups.map((g) => (
        <div key={g.key}>
          <div className="menu-label">{g.label}</div>
          {g.items.map((s) => {
            k++;
            const idx = k;
            return (
              <button
                key={s.id}
                className={clsx('menu-item', { active: nav.index === idx })}
                onMouseEnter={() => nav.setIndex(idx)}
                onClick={() => onPick(s)}
              >
                <span className="menu-item-icon">
                  <StatusIcon type={s.type} color={s.color} progress={statusProgress(statuses, s)} />
                </span>
                <span className="menu-item-label truncate status-menu-name">{s.name}</span>
                {s.id === currentId && <Check size={14} className="faint" />}
              </button>
            );
          })}
        </div>
      ))}
      {flat.length === 0 && <div className="menu-empty">No statuses found</div>}
    </div>
  );
}
